/**
 * Document 2 · Phase 10.3a — /billing/return
 *
 * After Stripe Checkout completes, Stripe redirects user here with
 * ?session_id=<CHECKOUT_SESSION_ID>. We poll GET /api/billing/checkout/status/:id
 * every 2s (up to ~30s) until payment_status='paid' or Stripe status='expired'.
 *
 * On success → redirect to /dashboard with a success toast.
 * On expiry/cancel → redirect to /pricing with a retry prompt.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';


const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const MAX_POLL_ATTEMPTS = 15;   // 15 × 2s = 30s
const POLL_INTERVAL_MS = 2000;


export default function CheckoutReturn() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState('polling'); // polling | paid | expired | error
  const [detail, setDetail] = useState(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState('error');
      setDetail('Missing session_id in URL');
      return;
    }
    if (!token) {
      setState('error');
      setDetail('You must be logged in to verify this checkout.');
      return;
    }
    stopRef.current = false;

    const poll = async (n) => {
      if (stopRef.current) return;
      try {
        const r = await axios.get(`${API}/billing/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${token}` } });
        setDetail(r.data);
        if (r.data.status === 'paid' || r.data.payment_status === 'paid') {
          setState('paid');
          toast.success('Subscription activated');
          setTimeout(() => navigate('/dashboard'), 2500);
          return;
        }
        if (r.data.status === 'expired' || r.data.stripe_status === 'expired') {
          setState('expired');
          return;
        }
        if (n + 1 >= MAX_POLL_ATTEMPTS) {
          setState('error');
          setDetail({ ...r.data, error: 'Timed out waiting for payment confirmation' });
          return;
        }
        setAttempt(n + 1);
        setTimeout(() => poll(n + 1), POLL_INTERVAL_MS);
      } catch (e) {
        setState('error');
        setDetail(e?.response?.data || e.message);
      }
    };
    poll(0);
    return () => { stopRef.current = true; };
  }, [sessionId, token, navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: 'linear-gradient(180deg, #fefefe 0%, #f5f0e8 100%)' }}
      data-testid="checkout-return-page"
    >
      <Card className="rounded-sm border max-w-md w-full">
        <CardContent className="p-10 text-center space-y-4">
          {state === 'polling' && (
            <>
              <Loader2 className="w-10 h-10 mx-auto animate-spin" style={{ color: '#B89B5E' }} />
              <h2 className="font-serif text-2xl">Activating your subscription…</h2>
              <p className="text-sm text-muted-foreground">
                This usually takes a few seconds. Hang tight — don't close this tab.
              </p>
            </>
          )}
          {state === 'paid' && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-serif text-2xl">You're in 🎉</h2>
              <p className="text-sm text-muted-foreground">
                Subscription activated. Redirecting to your dashboard…
              </p>
              {detail?.tier && (
                <div className="text-xs uppercase tracking-widest text-[#B89B5E]">
                  {detail.tier.charAt(0).toUpperCase() + detail.tier.slice(1)} plan
                  {detail.interval ? ` · ${detail.interval === 'yearly' ? 'Yearly' : 'Monthly'}` : ''}
                </div>
              )}
              <Link to="/dashboard">
                <Button className="rounded-sm" data-testid="checkout-return-dashboard-btn">
                  Go to dashboard
                </Button>
              </Link>
            </>
          )}
          {state === 'expired' && (
            <>
              <AlertTriangle className="w-10 h-10 mx-auto text-amber-500" />
              <h2 className="font-serif text-2xl">Checkout session expired</h2>
              <p className="text-sm text-muted-foreground">
                No charge was made. You can try again.
              </p>
              <Link to="/pricing">
                <Button className="rounded-sm">Back to pricing</Button>
              </Link>
            </>
          )}
          {state === 'error' && (
            <>
              <AlertTriangle className="w-10 h-10 mx-auto text-red-500" />
              <h2 className="font-serif text-2xl">Something went wrong</h2>
              <p className="text-sm text-muted-foreground">
                {typeof detail === 'string' ? detail : detail?.error || detail?.detail || 'Unable to verify checkout.'}
              </p>
              <div className="flex justify-center gap-2">
                <Link to="/pricing"><Button variant="outline" className="rounded-sm">Back to pricing</Button></Link>
                <Link to="/dashboard"><Button className="rounded-sm">Dashboard</Button></Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
